import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, GraduationCap, Award } from "lucide-react";
import { fetchProducts } from "@/lib/api";
import ProductCard, { ProductModal } from "@/components/ProductCard";
import LeadMagnet from "@/components/LeadMagnet";
import useMeta from "@/hooks/useMeta";

const TEDX_IMG = "/api/static/assets/4ad00f64_sj1bc2jx_52523387053_c5818956ae_c.jpg";
const PORTRAIT_IMG = "/api/static/assets/20f6598e_4zwrfg1m_Photoroom_20251102_153319.JPG";
const HeroBg = "https://images.unsplash.com/photo-1540575467063-178a50c2df87?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMHNlbWluYXIlMjBjcm93ZHxlbnwwfHx8fDE3ODMxMDYyNDF8MA&ixlib=rb-4.1.0&q=85";

const DIFFERENTIATORS = [
  "Resolving Organizational Conflict Using Emotional Intelligence Principles",
  "Building Tomorrow's Flexible, Agile, and Resilient Leaders Today",
  "Preparing Tomorrow's Leaders as Organizational and Transformational Change Agents",
  "Developing Today's People Skills that Flexibly Address Tomorrow's Leadership Challenges",
  "Creating Leadership Practices Based on Character, Accountability, and Authenticity",
];

const Home = () => {
  useMeta({
    title: "Global Leader Solutions — Character-Based Leadership Development",
    description: "Ebooks, courses, and coaching from Dr. Andrew H. Campbell — build the character every great leader needs. Certificate of completion with every course.",
    canonical: "https://globalleadersolutions.com/",
  });
  const [featured, setFeatured] = useState([]);
  const [refreshed, setRefreshed] = useState([]);
  const [selected, setSelected] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    fetchProducts().then((prods) => {
      const picks = [
        prods.find((p) => p.id === "eb_leadership_bundle"),
        prods.find((p) => p.id === "eb_self_bundle"),
        prods.find((p) => p.id === "c_leadership"),
        prods.find((p) => p.id === "c_conflict_bundle"),
      ].filter(Boolean);
      setFeatured(picks);
      setRefreshed(prods.filter((p) => p.new_edition));
    });
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative bg-[#FAF4F5] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={HeroBg} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-6 sm:pt-14 sm:pb-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7 section-fade">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#8C4A57] mb-3">
              Global Leader Solutions
            </div>

            {/* Marquis Who's Who credibility ribbon */}
            <div className="inline-flex items-center gap-3 bg-white border border-[#E8DCDD] rounded-full pl-2 pr-4 py-1.5 mb-4 shadow-sm"
              data-testid="hero-marquis-ribbon">
              <img
                src="/api/static/assets/574722db_ad8b7cso_Honours-14.jpg"
                alt="Marquis Who's Who in America badge"
                className="w-8 h-8 object-contain rounded-full" />
              <span className="text-[11px] sm:text-xs tracking-[0.14em] uppercase text-[#6E3944] font-medium">
                As listed in <span className="font-semibold">Marquis Who&apos;s Who in America</span>
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold text-[#2D2628] leading-[1.05] tracking-tight">
              Resolving Organizational Discourse Using <span className="text-[#8C4A57] italic">Emotional Intelligence</span>
            </h1>
            <p className="mt-4 text-base sm:text-lg text-[#6B5E62] max-w-xl leading-relaxed font-semibold uppercase tracking-wider">
              Leadership Masterclass
            </p>
            <p className="mt-3 text-sm sm:text-base text-[#6B5E62] max-w-xl leading-relaxed">
              Global Leader Solutions, under the auspice of the Global Leadership Education and Training Institute,
              provides accessible, high-quality digital learning and leadership education focused on
              emotional intelligence, conflict management, and organizational effectiveness. Through
              ebooks, online courses, and coaching programs, we equip emerging senior executive
              decision-makers, corporate directors, HR professionals, and inspiring leaders with the
              applicational knowledge, skills, and abilities to prevent, navigate, and resolve
              organizational discourse using emotional intelligence principles — improving communication,
              reducing workplace conflict, and strengthening leadership capacity for business growth.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/courses" data-testid="hero-cta-courses" className="btn-primary">
                Explore Courses <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <Link to="/ebooks" data-testid="hero-cta-ebooks" className="btn-outline">Browse Ebooks</Link>
            </div>
          </div>
          <div className="lg:col-span-5 hidden lg:block">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#D9C5C9] opacity-40 rounded-md" />
              <img
                src={TEDX_IMG}
                alt="Dr. Andrew Campbell speaking at TEDx"
                data-testid="hero-tedx-image"
                className="relative w-full h-[520px] object-cover rounded-sm shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Value props (unchanged) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 section-fade">
          {[
            { icon: BookOpen, title: "Field-tested Ebooks", body: "Practical, workbook-driven ebooks that translate leadership theory into daily practice." },
            { icon: GraduationCap, title: "Video Courses & Certificates", body: "Structured programs with video modules, downloadable workbooks, and completion certificates." },
            { icon: Award, title: "Coaching & Masterclasses", body: "Cohort programs and coaching that turn frameworks into everyday leadership behavior." },
          ].map(({ icon: Icon, title, body }, i) => (
            <div key={i} className="border-l-2 border-[#D9C5C9] pl-6" data-testid={`value-prop-${i}`}>
              <Icon className="w-6 h-6 text-[#8C4A57] mb-3" />
              <h3 className="text-xl font-serif font-semibold text-[#2D2628] mb-1.5">{title}</h3>
              <p className="text-sm text-[#6B5E62] leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Core Values */}
      <section className="bg-[#FAF4F5] border-b border-[#E8DCDD]" data-testid="core-values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-3xl mb-8">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-2">Our Foundation</div>
            <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-[#2D2628] leading-tight">
              Core values
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
            {[
              {
                title: "Integrity in Leadership",
                body: "We commit to believe leadership begins with character. We commit to ethical decision-making, accountability, and transparency in how we lead, teach, and develop leadership tools.",
              },
              {
                title: "Emotional Intelligence and Self-Awareness",
                body: "We commit to cultivating leaders who practice reflection, empathy, and emotional discipline to build stronger relationships and more resilient teams.",
              },
              {
                title: "Constructive Conflict Engagement",
                body: "We commit to equip leaders with the competence and frameworks necessary to navigate and resolve organizational discourse with respect, clarity, and relational intelligence.",
              },
              {
                title: "Innovation with Purpose",
                body: "We commit to integrate the development of technologies with proven leadership science to make high-quality learning and leadership development more accessible and scalable.",
              },
              {
                title: "Service and Global Impact",
                body: "We commit to develop leaders who contribute positively to their organizations, communities, and society.",
              },
            ].map(({ title, body }, i) => (
              <div key={i} data-testid={`core-value-${i}`} className="flex gap-4 items-start">
                <span className="mt-1 shrink-0 w-10 h-10 rounded-sm bg-white border border-[#E8DCDD] text-[#8C4A57] font-serif font-semibold flex items-center justify-center text-sm">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="text-xl font-serif font-semibold text-[#2D2628] mb-1.5 leading-snug">{title}</h3>
                  <p className="text-sm text-[#6B5E62] leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why We're Different */}
      <section className="bg-white border-y border-[#E8DCDD]" data-testid="why-different-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-2">Our Difference</div>
            <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-[#2D2628] leading-tight mb-3">
              Why we're different
            </h2>
            <p className="text-sm text-[#6B5E62] leading-relaxed">
              Unlike typical online programs, we focus on equipping emerging leaders, along with
              organizational and strategic leaders, with the tools to resolve organizational
              discourse through emotional intelligence principles, thereby creating a psychologically
              safe organizational culture.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="space-y-3">
              {DIFFERENTIATORS.map((line, i) => (
                <li key={i} data-testid={`diff-item-${i}`} className="flex gap-3 items-start">
                  <span className="mt-1 flex-shrink-0 w-8 h-8 rounded-sm bg-[#FAF4F5] text-[#8C4A57] font-serif font-semibold flex items-center justify-center text-sm">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-[#2D2628] leading-relaxed font-serif text-lg">{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Tailored Courses / Quote / Practical Leadership */}
      <section className="bg-[#FAF4F5]" data-testid="tailored-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-2">Offerings</div>
            <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-[#2D2628] leading-tight mb-4">
              Offered tailored courses on
            </h2>
            <ul className="space-y-2 mb-5">
              {["Leadership and Management",
                "Organizational Conflict Management and Conflict Resolution",
                "Emotional Intelligence"].map((c, i) => (
                <li key={i} data-testid={`tailored-course-${i}`}
                  className="flex gap-3 items-start text-[#2D2628] font-serif text-lg">
                  <span className="text-[#8C4A57] mt-1">•</span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
            <blockquote className="border-l-4 border-[#8C4A57] pl-5 py-2 my-5" data-testid="campbell-quote">
              <p className="font-serif italic text-xl text-[#2D2628] leading-snug">
                "The leaders who get promoted are the ones who can manage conflict without creating casualties."
              </p>
              <footer className="mt-2 text-xs uppercase tracking-widest text-[#6B5E62]">— Dr. Andrew Campbell</footer>
            </blockquote>
          </div>
          <div className="lg:col-span-7">
            <h3 className="text-2xl sm:text-3xl font-serif font-semibold text-[#2D2628] leading-tight mb-3">
              Practical leadership competencies for conflict resolution and emotional intelligence
            </h3>
            <div className="space-y-3 text-[#6B5E62] leading-relaxed text-sm sm:text-base">
              <p>
                Workplace drama drains energy, fractures trust, and slows progress. Most leaders aren't
                struggling because they lack talent — they're struggling because they were never taught
                how to navigate organizational discourse using emotional intelligence frameworks.
              </p>
              <p>
                At Global Leader Solutions, we equip leaders with practical, research-backed outcomes to
                turn organizational tension into collaboration, strengthen team relationships, and build
                an organizational culture where people feel safe to speak up and do their most productive work.
              </p>
              <p className="text-[#2D2628] font-medium">
                If you're ready to lead with courage, clarity, and emotional maturity, you're in the right place.
              </p>
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/courses" data-testid="practical-cta-courses" className="btn-primary">
                Start with a Course
              </Link>
              <Link to="/contact" data-testid="practical-cta-contact" className="btn-outline">
                Talk to Dr. Campbell
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Just Refreshed - auto-hidden when no new_edition products */}
      {refreshed.length > 0 && (
        <section className="py-10 bg-[#FAF4F5] border-y border-[#E8DCDD]" data-testid="just-refreshed-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
              <div>
                <div className="inline-flex items-center gap-2 mb-2">
                  <span className="w-2 h-2 rounded-full bg-[#8C4A57] animate-pulse" />
                  <span className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57]">Just Refreshed</span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-[#2D2628]">New editions this month</h2>
                <p className="text-sm text-[#6B5E62] mt-1.5 max-w-2xl">
                  Refreshed covers, sharper positioning, and updated frameworks — see what's just been reissued.
                </p>
              </div>
              <Link to="/ebooks" className="text-sm text-[#8C4A57] font-semibold uppercase tracking-wider hover:text-[#703B45]" data-testid="home-refreshed-view-all">
                Browse ebooks →
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {refreshed.slice(0, 4).map((p) => (
                <ProductCard key={p.id} product={p} onOpen={(prod) => { setSelected(prod); setModalOpen(true); }} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured products */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-2">Featured</div>
              <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-[#2D2628]">Programs to begin with</h2>
            </div>
            <Link to="/courses" className="text-sm text-[#8C4A57] font-semibold uppercase tracking-wider hover:text-[#703B45]" data-testid="home-view-all-link">
              View all →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {featured.map((p) => (
              <ProductCard key={p.id} product={p} onOpen={(prod) => { setSelected(prod); setModalOpen(true); }} />
            ))}
          </div>
          <ProductModal product={selected} open={modalOpen} onOpenChange={setModalOpen} />
        </div>
      </section>

      {/* Faculty tease */}
      <section className="bg-[#FAF4F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-2">The Instructor</div>
            <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-[#2D2628] mb-3">Dr. Andrew Campbell</h2>
            <p className="text-base text-[#6B5E62] leading-relaxed mb-3">
              Retired senior military officer and international leadership expert. PhD in Global Leadership.
              TEDx Speaker. 2024 Who's Who of America Inductee. International speaker and published author.
            </p>
            <Link to="/faculty" className="text-sm text-[#8C4A57] font-semibold uppercase tracking-wider hover:text-[#703B45]" data-testid="home-faculty-link">
              Read the full bio →
            </Link>
          </div>
          <div>
            <img src={PORTRAIT_IMG} alt="Dr. Andrew Campbell"
              className="w-full h-[400px] object-cover rounded-sm shadow-md" />
          </div>
        </div>
      </section>

      <LeadMagnet />
    </div>
  );
};

export default Home;
